/**
 * <SuggestionsBox /> — Affiche les axes de travail suggérés par le moteur
 * d'inférence pédagogique (evalInference.js).
 *
 * Les suggestions citent EXPLICITEMENT le Livret d'apprentissage REMC :
 *  - code officiel (badge ex C2-d) avec couleur par catégorie
 *  - libellé livret
 *  - objectif reformulé « Savoir… »
 *  - objectif officiel + critères de réalisation (toggle)
 *  - 3 boutons de validation enseignant (À travailler / En cours / Acquis)
 *    → trace de séance stockée dans la fiche, à reporter au livret élève
 *  - bouton « Adopter cet objectif » (recopie dans le champ Objectif)
 *  - lien « Voir dans le livret » (deep-link /livret#C2-d)
 *  - lien « Fiche cours » si un slug est disponible
 *  - liens vers articles magazine / enquêtes ETM additionnels
 *
 * Props :
 *  - suggestions : array — Sortie de inferFromPlateau / inferFromCirculation
 *  - onAdoptObjectif(text) : recopie l'objectif dans le champ "Objectif"
 *  - onValidateLivret(code, status) : callback validation enseignant (optionnel)
 *  - livretValidations : object { 'C1-e': 'acquis', … } (validations courantes)
 *  - fichesBaseRoute : ex '/enseignant/terrain' pour construire l'URL fiche
 *  - accent : couleur d'accent (param du formulaire)
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, ArrowRight, ClipboardCheck, BookOpen, ListChecks,
  ChevronDown, ChevronUp, FileText, Circle, Clock, CheckCircle2, Newspaper, Radio,
} from 'lucide-react';
import { Button } from '../ui/button';

const CATEGORIE_COLORS = {
  C1: '#0891b2', // bleu maniement
  C2: '#7c3aed', // violet circulation
  C3: '#dc2626', // rouge difficile
  C4: '#16a34a', // vert autonomie
};

const STATUS_META = {
  non_acquis: { label: 'À travailler', short: 'À travailler', color: '#64748b', Icon: Circle },
  en_cours:   { label: 'En cours',     short: 'En cours',     color: '#d97706', Icon: Clock },
  acquis:     { label: 'Acquis',       short: 'Acquis',       color: '#16a34a', Icon: CheckCircle2 },
};
const STATUS_ORDER = ['non_acquis', 'en_cours', 'acquis'];

function ValidationButtons({ code, currentStatus, onChange, accentColor }) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid={`validation-${code}`}>
      <span className="text-[10px] font-black uppercase tracking-wider text-muted-foreground mr-1">
        Statut séance :
      </span>
      {STATUS_ORDER.map((st) => {
        const meta = STATUS_META[st];
        const active = currentStatus === st;
        const Icon = meta.Icon;
        return (
          <button
            key={st}
            type="button"
            onClick={() => onChange(active ? null : st)}
            data-testid={`validation-${code}-${st}`}
            aria-pressed={active}
            className="inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-black uppercase tracking-wider transition-colors border"
            style={
              active
                ? { background: meta.color, color: '#fff', borderColor: meta.color }
                : { background: 'transparent', color: meta.color, borderColor: `${meta.color}55` }
            }
          >
            <Icon className="w-3 h-3" />
            {meta.short}
          </button>
        );
      })}
      {currentStatus && (
        <span
          className="text-[9px] italic text-muted-foreground ml-1"
          aria-hidden="true"
          style={{ color: accentColor }}
        >
          (à reporter au livret élève)
        </span>
      )}
    </div>
  );
}

function SuggestionRow({
  s,
  index,
  accent,
  onAdoptObjectif,
  onValidateLivret,
  livretValidations,
  fichesBaseRoute,
}) {
  const [open, setOpen] = useState(false);
  const catColor = CATEGORIE_COLORS[s.categorie] || accent;
  // ficheRoute est désormais construit en amont par evalInference (hub C1=plateau, C2=seance-circulation, etc.)
  // On garde le fallback historique pour rétro-compat si une suggestion legacy n'a pas ficheRoute.
  const ficheUrl = s.ficheRoute || (s.slug ? `${fichesBaseRoute}/${s.slug}` : null);
  const hasFiche = !!ficheUrl;
  const hasContenus = (s.contenus || []).length > 0;
  const currentStatus = livretValidations?.[s.competenceCode] || null;
  const statusMeta = currentStatus ? STATUS_META[currentStatus] : null;

  return (
    <li
      className="rounded-xl border-2 bg-card p-3 sm:p-4 transition-colors"
      style={{ borderColor: `${catColor}40` }}
      data-testid={`suggestion-${index}`}
    >
      <div className="flex items-start gap-2.5">
        {/* Rang */}
        <span
          className="w-7 h-7 rounded-full flex items-center justify-center text-white font-black text-xs flex-shrink-0 mt-0.5"
          style={{ background: catColor }}
          aria-hidden="true"
        >
          {index + 1}
        </span>

        <div className="flex-1 min-w-0">
          {/* En-tête : badge REMC + source + (statut courant) */}
          <div className="flex items-center gap-1.5 flex-wrap mb-1">
            <span
              className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white"
              style={{ background: catColor }}
              data-testid={`suggestion-${index}-code`}
            >
              REMC · {s.competenceCode}
            </span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
              {s.source}
            </span>
            {statusMeta && (
              <span
                className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-wider text-white ml-auto"
                style={{ background: statusMeta.color }}
                data-testid={`suggestion-${index}-status`}
              >
                <statusMeta.Icon className="w-3 h-3" />
                {statusMeta.label}
              </span>
            )}
          </div>

          {/* Intitulé officiel livret */}
          <p
            className="text-[11px] font-semibold leading-snug mb-1"
            style={{ color: catColor }}
            data-testid={`suggestion-${index}-intitule`}
          >
            {s.competenceIntitule}
          </p>

          {/* Objectif COURT (verbe d'action, concis, mesurable) */}
          <p
            className="text-base font-black leading-tight mt-1"
            data-testid={`suggestion-${index}-objectif`}
          >
            {s.objectifCourt || s.objectifSavoir}
          </p>

          {/* Critères PRÉCIS (mesurables, observables) — visibles direct */}
          {(s.criteresPrecis?.length > 0) && (
            <ul
              className="mt-2 space-y-1"
              data-testid={`suggestion-${index}-criteres`}
            >
              {s.criteresPrecis.map((c, i) => (
                <li
                  key={i}
                  className="text-[12px] leading-snug flex gap-1.5 items-start"
                >
                  <span
                    className="inline-block w-1 h-1 rounded-full mt-1.5 flex-shrink-0"
                    style={{ background: catColor }}
                    aria-hidden="true"
                  />
                  <span className="flex-1">{c}</span>
                </li>
              ))}
            </ul>
          )}

          {/* Toggle détails livret OFFICIEL (consultation, pas pour l'éval rapide) */}
          {(s.criteresLivret?.length > 0 || s.objectifLivret) && (
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              className="mt-2 inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-muted-foreground hover:text-foreground transition-colors"
              data-testid={`suggestion-${index}-toggle`}
              aria-expanded={open}
            >
              {open ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              {open ? 'Masquer le libellé officiel REMC' : 'Voir le libellé officiel REMC'}
            </button>
          )}

          {/* Détails repliables : objectif officiel + critères REMC complets */}
          {open && (
            <div className="mt-2 space-y-2 rounded-lg border border-border bg-background/50 p-2.5">
              {s.objectifLivret && (
                <div>
                  <div
                    className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider mb-0.5"
                    style={{ color: catColor }}
                  >
                    <FileText className="w-3 h-3" /> Formulation officielle REMC
                  </div>
                  <p className="text-[11px] leading-relaxed italic text-muted-foreground">
                    {s.objectifLivret}
                  </p>
                </div>
              )}
              {s.criteresLivret?.length > 0 && (
                <div>
                  <div
                    className="flex items-center gap-1 text-[10px] font-black uppercase tracking-wider mb-0.5"
                    style={{ color: catColor }}
                  >
                    <ListChecks className="w-3 h-3" /> Critères livret (intégral)
                  </div>
                  <ul className="space-y-0.5">
                    {s.criteresLivret.slice(0, 4).map((c, i) => (
                      <li key={i} className="text-[11px] leading-snug flex gap-1.5">
                        <span style={{ color: catColor }}>•</span>
                        <span className="flex-1">{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* Validation enseignant (statut séance) */}
          {onValidateLivret && (
            <div className="mt-3 pt-2.5 border-t border-border/40">
              <ValidationButtons
                code={s.competenceCode}
                currentStatus={currentStatus}
                onChange={(st) => onValidateLivret(s.competenceCode, st)}
                accentColor={catColor}
              />
            </div>
          )}

          {/* Actions principales */}
          <div className="flex flex-wrap gap-1.5 mt-2.5">
            <Button
              type="button"
              size="sm"
              asChild
              className="gap-1.5 h-7 text-xs text-white"
              style={{ background: catColor }}
              data-testid={`launch-seance-${index}`}
            >
              <Link to={`/enseignant/seance-live/${s.competenceCode}`}>
                <Radio className="w-3 h-3" /> Lancer la séance
                <ArrowRight className="w-3 h-3" />
              </Link>
            </Button>

            {onAdoptObjectif && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => onAdoptObjectif(s.objectifSavoir)}
                className="gap-1.5 h-7 text-xs"
                style={{ borderColor: `${catColor}55`, color: catColor }}
                data-testid={`adopt-${index}`}
              >
                <ClipboardCheck className="w-3 h-3" /> Adopter cet objectif
              </Button>
            )}

            <Button
              type="button"
              size="sm"
              variant="outline"
              asChild
              className="gap-1.5 h-7 text-xs"
              data-testid={`open-livret-${index}`}
            >
              <Link to={s.livretAnchor}>
                <BookOpen className="w-3 h-3" /> Voir dans le livret
              </Link>
            </Button>

            {hasFiche && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                asChild
                className="gap-1.5 h-7 text-xs"
                data-testid={`open-fiche-${index}`}
              >
                <Link to={ficheUrl}>
                  <FileText className="w-3 h-3" /> Fiche cours
                  <ArrowRight className="w-3 h-3" />
                </Link>
              </Button>
            )}
          </div>

          {/* Contenus additionnels (articles / enquêtes) */}
          {hasContenus && (
            <div className="mt-2 pt-2 border-t border-border/30">
              <div
                className="text-[10px] font-black uppercase tracking-wider mb-1.5 flex items-center gap-1"
                style={{ color: catColor }}
              >
                <Newspaper className="w-3 h-3" /> Contenus liés
              </div>
              <div className="flex flex-wrap gap-1">
                {s.contenus.map((c, i) => {
                  const to = c.type === 'magazine' ? `/magazine/${c.slug}` : `/etm/${c.slug}`;
                  return (
                    <Link
                      key={`${c.slug}-${i}`}
                      to={to}
                      className="inline-flex items-center gap-1 px-2 py-1 rounded-md text-[10px] font-bold border transition-colors hover:bg-muted"
                      style={{ borderColor: `${catColor}40`, color: catColor }}
                      data-testid={`contenu-${index}-${i}`}
                    >
                      {c.type === 'magazine' ? '📰' : '🔍'} {c.label}
                    </Link>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </li>
  );
}

export default function SuggestionsBox({
  suggestions,
  onAdoptObjectif,
  onValidateLivret,
  livretValidations,
  fichesBaseRoute = '/enseignant/terrain',
  accent = '#ea580c',
}) {
  if (!suggestions || suggestions.length === 0) {
    return (
      <div
        className="rounded-2xl border-2 border-dashed border-border p-4 text-center text-xs text-muted-foreground italic"
        data-testid="suggestions-empty"
      >
        Coche au moins une case <span className="font-bold">« Non »</span> dans les critères
        ou compétences observées pour voir apparaître ici les axes de travail prioritaires
        suggérés automatiquement (raccordés au livret REMC).
      </div>
    );
  }

  return (
    <div
      className="rounded-2xl border-2 p-4 sm:p-5 space-y-3"
      style={{
        background: `linear-gradient(135deg, ${accent}0d 0%, transparent 100%)`,
        borderColor: `${accent}55`,
      }}
      data-testid="suggestions-box"
    >
      <div className="flex items-center gap-2 mb-0.5">
        <Sparkles className="w-4 h-4" style={{ color: accent }} />
        <h3 className="text-sm font-black tracking-tight" style={{ color: accent }}>
          {suggestions.length} {suggestions.length > 1 ? 'axes prioritaires détectés' : 'axe prioritaire détecté'}
        </h3>
      </div>
      <p className="text-[11px] italic text-muted-foreground -mt-1">
        Axes raccordés au <strong>Livret d&apos;apprentissage REMC</strong> (catégorie A). Le statut « séance » est une trace
        pédagogique imprimée sur la fiche, à reporter par l&apos;élève dans son livret personnel.
      </p>

      <ol className="space-y-2.5">
        {suggestions.map((s, i) => (
          <SuggestionRow
            key={`${s.competenceCode}-${i}`}
            s={s}
            index={i}
            accent={accent}
            onAdoptObjectif={onAdoptObjectif}
            onValidateLivret={onValidateLivret}
            livretValidations={livretValidations}
            fichesBaseRoute={fichesBaseRoute}
          />
        ))}
      </ol>
    </div>
  );
}
